/**
 * appGuarita.js — o manual do app da guarita, dentro da Central de Ajuda
 * CI Sistemas de Informática Ltda. — AcessoiD (Onda 92)
 *
 * Mesma estrutura de `plataforma.js`, e pelo mesmo motivo: conteúdo como dado,
 * filtrável por perfil.
 *
 * POR QUE ESTE MANUAL EXISTE AQUI, E NÃO SÓ NO REPOSITÓRIO
 *
 * `docs/MANUAL_DO_APP_GUARITA.md` é ótimo para quem tem o repositório. Quem
 * está na guarita não tem — tem o navegador aberto na plataforma. Um manual
 * que o operador não alcança no momento da dúvida não é manual.
 *
 * O texto veio do arquivo do repositório e foi reorganizado por perfil. As duas
 * fontes dizem a mesma coisa; se divergirem um dia, a do repositório é a de
 * referência para quem desenvolve, e esta é a que o operador lê.
 */

export const MANUAL_APP = {
  id: 'app',
  titulo: 'Manual do App da Guarita',
  subtitulo: 'O que se faz em pé, com o carro na frente',
  resumo:
    'O app é para o que acontece com o veículo à sua frente: corrigir uma placa lida errado, ' +
    'abrir ticket quando a câmera não viu, cobrar, conferir o pátio. O trabalho de mesa — ' +
    'tarifa, cadastro, relatório, estorno — continua no computador.',

  secoes: [
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'instalar',
      titulo: 'Instalar no celular',
      blocos: [
        { tipo: 'p', texto:
          'O app não vem de loja. Ele é distribuído pela própria plataforma, para os aparelhos da ' +
          'guarita — o botão de download está no topo desta Central de Ajuda.' },
        { tipo: 'passos', itens: [
          'No celular da guarita, abra a plataforma e vá em <b>Central de Ajuda</b>.',
          'Toque em <b>Baixar o app</b>. São cerca de 55 MB: prefira o Wi-Fi.',
          'O Android vai avisar que o arquivo vem de fora da loja. Autorize a instalação para o navegador quando ele pedir.',
          'Abra o arquivo baixado e confirme a instalação.',
        ] },
        { tipo: 'aviso', tom: 'dica', titulo: 'O aviso do Android é esperado',
          texto:
            'Instalar um app que não vem da Play Store exige autorizar a origem uma vez. É a tela ' +
            '"Permitir desta fonte". Depois de autorizada, as atualizações seguintes não pedem de novo.' },
        { tipo: 'aviso', tom: 'atencao', titulo: 'Atualizações avisam, mas não se instalam sozinhas',
          texto:
            'Quando sai uma versão nova, o app mostra uma faixa avisando. Ele <b>não</b> baixa nem ' +
            'instala por conta própria — você toca, e o Android conduz com a tela de sempre. ' +
            'Instalar pelas costas de quem opera, num aparelho compartilhado, é o tipo de coisa ' +
            'que ninguém sabe explicar depois.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'primeira-vez',
      titulo: 'A primeira vez',
      blocos: [
        { tipo: 'subtitulo', texto: 'Entrar' },
        { tipo: 'p', texto: 'Ao abrir o app pela primeira vez, ele pergunta três coisas:' },
        { tipo: 'tabela',
          cabecalho: ['Campo', 'O que colocar'],
          linhas: [
            ['Servidor', '<code>https://g2.acessoid.com.br</code> — o endereço da sua instalação'],
            ['Usuário', 'o mesmo que você usa no sistema pelo computador'],
            ['Senha', 'a mesma'],
          ] },
        { tipo: 'p', texto:
          'O endereço é pedido porque o mesmo app atende mais de um estacionamento. Você o digita ' +
          'uma vez só: ele fica guardado.' },

        { tipo: 'subtitulo', texto: 'Definir o PIN' },
        { tipo: 'p', texto:
          'Depois de entrar, <b>defina um PIN</b> no menu do canto superior direito ' +
          '(⋮ → <i>Definir um PIN</i>). São pelo menos quatro dígitos.' },
        { tipo: 'aviso', tom: 'perigo', titulo: 'Por que isso importa',
          texto:
            'O celular fica na guarita, não no seu bolso. Sem PIN, quem pegar o aparelho opera no ' +
            '<b>seu nome</b> — cobra, isenta, corrige placa — e o sistema registra tudo como se ' +
            'fosse você.' },
        { tipo: 'p', texto:
          'Com o PIN, a tela bloqueia sozinha depois de <b>três minutos parado</b>. Enquanto você ' +
          'está atendendo, ela nunca bloqueia. Para sair por um instante, use ⋮ → <b>Bloquear ' +
          'agora</b>, que é mais rápido do que esperar.' },
        { tipo: 'aviso', tom: 'dica', titulo: 'Esqueceu o PIN?',
          texto: 'Na tela de bloqueio há <i>"Esqueci o PIN"</i>, que pede seu usuário e senha de novo. Você não fica preso.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'patio',
      titulo: 'A tela do Pátio',
      blocos: [
        { tipo: 'p', texto:
          'É a tela principal. Mostra <b>todos os veículos no pátio, em ordem de chegada</b>, o mais ' +
          'recente no topo. Cada linha traz a placa desenhada como ela é no para-brisa — Mercosul ' +
          'com a tarja azul, o padrão anterior em cinza —, o número do ticket e há quanto tempo o ' +
          'veículo está lá.' },
        { tipo: 'figura', src: 'app-patio.png', legenda: 'A tela do Pátio, com os veículos em ordem de chegada.' },
        { tipo: 'tabela',
          cabecalho: ['Etiqueta', 'Significa'],
          linhas: [
            ['<b>acabou de entrar</b> (verde)', 'chegou nos últimos três minutos'],
            ['<b>mensalista</b> / <b>credenciado</b> (verde)', '<b>não passa pelo caixa</b>'],
            ['<b>sem câmera</b> (âmbar)', 'o ticket foi aberto à mão'],
          ] },
        { tipo: 'p', texto:
          'A etiqueta de mensalista e credenciado existe para você não perder tempo: ao ver essa ' +
          'marca, já sabe que não há o que cobrar.' },
        { tipo: 'aviso', tom: 'dica', titulo: 'Procurar não exige a placa inteira',
          texto: 'Use a busca no topo e digite os últimos dígitos que você lembra — a lista filtra.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'impressora-bluetooth',
      titulo: 'Imprimir numa mini impressora Bluetooth',
      blocos: [
        { tipo: 'p', texto:
          'Quando a guarita não tem coletor com impressora embutida, o app imprime numa <b>mini ' +
          'impressora térmica de bolso</b>, pareada com o celular. O ticket sai igual: mesmo ' +
          'desenho, mesma placa emoldurada, mesmo QR.' },
        { tipo: 'p', texto:
          'São <b>dois ajustes, em lugares diferentes</b>. Nas Configurações, pelo computador, ' +
          'escolhe-se <b>como</b> se imprime — vale para o estacionamento inteiro. No app, em cada ' +
          'celular, escolhe-se <b>qual</b> impressora — porque cada guarita tem a sua.' },
        { tipo: 'tabela',
          cabecalho: ['Onde', 'O que se define'],
          linhas: [
            ['Configurações › Impressão do Ticket (computador)', 'o modo <b>Mini impressora Bluetooth (escolhida no app)</b>, a largura do papel, o cabeçalho e o rodapé'],
            ['App › menu ⋮ do Pátio › <b>Impressora Bluetooth</b>', 'qual impressora recebe os tickets <b>deste</b> celular'],
          ] },
        { tipo: 'p', texto:
          '<b>No celular, uma vez só:</b> pareie a impressora em Configurações do Android › ' +
          'Bluetooth (se pedir senha, costuma ser <code>0000</code> ou <code>1234</code>); depois ' +
          'abra o app em ⋮ › Impressora Bluetooth, toque nela na lista e imprima o ticket de ' +
          'amostra. Na primeira vez o Android pede permissão para usar o Bluetooth — autorize.' },
        { tipo: 'aviso', tom: 'atencao', titulo: 'O papel precisa ser rolo contínuo — e o MODO também',
          texto: 'Muitas dessas impressoras servem etiquetas adesivas <b>ou</b> rolo contínuo, e têm um ' +
            'modo para cada um. O ticket pede <b>rolo contínuo de 57 ou 58 mm, no modo contínuo</b>. No ' +
            'modo errado acontece uma de duas coisas, e nenhuma aparece como erro na tela: ou ela anda ' +
            'muito papel e acende ERR, procurando o intervalo entre etiquetas que o rolo não tem, ou ' +
            '<b>não faz absolutamente nada</b> — no modo etiqueta ela ignora em silêncio o que o app ' +
            'manda. <b>Para saber em que modo ela está:</b> com a impressora desligada, segure energia e ' +
            'avanço juntos; sai uma página de teste, e nela a linha <b>Mode:</b> diz a resposta. Se ' +
            'disser TSPL ou CPCL, está em modo etiqueta.' },
        { tipo: 'aviso', tom: 'atencao', titulo: 'Qual impressora comprar',
          texto: 'Procure <b>ESC/POS</b> e <b>recibo</b> (ou cupom) na descrição, e evite as anunciadas ' +
            'como <b>impressora de etiquetas</b>. O teste de aceitação leva um minuto: ponha o rolo ' +
            'contínuo, escolha a impressora no app e toque em <b>Avançar papel</b>. Se o papel andar, ' +
            'ela serve.' },
        { tipo: 'aviso', tom: 'dica', titulo: 'Cada celular aponta para a sua',
          texto: 'A escolha fica guardada no aparelho. Numa guarita com dois celulares, cada um ' +
            'imprime na própria impressora, e um não atrapalha o outro.' },
        { tipo: 'aviso', tom: 'atencao', titulo: 'O ticket não sai e nenhum erro aparece?',
          texto: 'Use <b>Avançar papel (teste simples)</b>, o botão acima do de amostra: ele manda à ' +
            'impressora só o comando de andar com o papel, sem desenho nenhum. Se o papel <b>andar</b>, ' +
            'a conexão está boa e ela entende o app — o problema está no papel ou no modo dele. Se ' +
            '<b>não andar</b>, ela ignorou o comando mais simples que existe: esse modelo não fala a ' +
            'mesma língua do app, e o caminho é uma impressora que declare <b>ESC/POS</b>.' },
        { tipo: 'aviso', tom: 'dica', titulo: 'Qual versão está neste aparelho',
          texto: 'Menu ⋮ do Pátio › <b>Sobre</b>: versão do app, servidor, usuário e a impressora ' +
            'escolhida. Numa guarita com vários aparelhos, é como se descobre qual ficou para trás.' },
        { tipo: 'aviso', tom: 'dica', titulo: 'Quem tem coletor não precisa mexer em nada',
          texto: 'No coletor com impressora embutida o papel continua saindo por ela, mesmo que o ' +
            'estacionamento tenha escolhido o modo da mini impressora para as outras guaritas.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'placa-errada',
      titulo: 'Quando a placa está errada',
      blocos: [
        { tipo: 'p', texto:
          'É o motivo principal de o app existir. Você está olhando para o carro; o sistema leu a ' +
          'placa de uma foto.' },
        { tipo: 'passos', itens: [
          'Toque no <b>lápis</b> na linha do veículo.',
          'O teclado da placa abre com a leitura atual.',
          'Corrija e confirme.',
        ] },
        { tipo: 'figura', src: 'app-corrigir.png', legenda: 'A tela de correção: o ticket, a placa lida e o aviso de que a correção vale para o ticket.' },
        { tipo: 'figura', src: 'app-teclado.png', legenda: 'Apagado um caractere, o teclado mostra SÓ números — a sétima posição da placa é numérica. E o Confirmar desabilita enquanto faltar dígito.' },
        { tipo: 'figura', src: 'app-placa-alterada.png', legenda: 'Placa completa e válida: o Confirmar acende.' },
        { tipo: 'p', texto:
          '<b>O teclado muda sozinho:</b> nas três primeiras posições mostra só letras, na quarta só ' +
          'números, e assim por diante. Você não precisa procurar a tecla. Embaixo da placa, o app ' +
          'diz quantos caracteres faltam e se o formato está válido.' },
        { tipo: 'aviso', tom: 'atencao', titulo: 'A correção vale para o ticket, não só para a leitura',
          texto: 'A cobrança, os relatórios e a saída pela câmera passam a usar a placa certa.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'nao-leu',
      titulo: 'Quando a câmera não leu o veículo',
      blocos: [
        { tipo: 'p', texto: 'O carro entrou, mas não apareceu na lista. Duas formas de resolver.' },

        { tipo: 'subtitulo', texto: 'Ler com a câmera do celular' },
        { tipo: 'p', texto:
          'Toque no <b>botão redondo com o ícone de câmera</b>. Aponte para a placa, encaixando-a na ' +
          'moldura, e toque em <i>Fotografar a placa</i>.' },
        { tipo: 'p', texto:
          'O app manda a foto para o <b>mesmo sistema que lê as câmeras fixas</b> — por isso a ' +
          'leitura tem a mesma qualidade. O resultado aparece para você <b>conferir antes de ' +
          'aceitar</b>: a foto de celular tem ângulo e luz piores, e a conferência é sua.' },

        { tipo: 'subtitulo', texto: 'Digitar' },
        { tipo: 'p', texto: 'Toque em <b>Ticket à mão</b> e digite a placa.' },
        { tipo: 'aviso', tom: 'dica', titulo: 'Quando digitar é o caminho certo',
          texto:
            'Placa suja, amassada ou obstruída — nenhum sistema de leitura resolve isso, e digitar é ' +
            'mais rápido que insistir com a câmera.' },
        { tipo: 'p', texto:
          '<b>Se a placa já estiver no pátio</b>, o app avisa e mostra qual é o ticket. Ele não ' +
          'deixa criar dois tickets do mesmo carro.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'contingencia',
      titulo: 'Quando as câmeras param',
      blocos: [
        { tipo: 'p', texto:
          'Se aparecer uma <b>faixa âmbar no topo</b> dizendo que uma câmera está sem sinal, o ' +
          'estacionamento entrou em <b>contingência</b>:' },
        { tipo: 'lista', itens: [
          '<b>Câmera de entrada sem sinal:</b> nenhum ticket nasce sozinho. Abra todos à mão, com a câmera do celular ou digitando.',
          '<b>Câmera de saída sem sinal:</b> as saídas precisam ser liberadas manualmente.',
        ] },
        { tipo: 'aviso', tom: 'atencao', titulo: 'A faixa avisa; ela não conserta',
          texto: 'Ela some sozinha quando as câmeras voltam. Avise a gestão — o app avisa você, mas não conserta a câmera.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'caixa-app',
      titulo: 'O caixa pelo app',
      perfis: ['operador', 'gestor'],
      blocos: [
        { tipo: 'p', texto: 'Toque no ícone de <b>caixa registradora</b> no topo.' },

        { tipo: 'subtitulo', texto: 'Abrir' },
        { tipo: 'p', texto:
          'No começo do turno, informe <b>quanto há em dinheiro na gaveta</b>. Esse valor é o ponto ' +
          'de partida da conferência. Com o caixa aberto, a tela mostra no alto a faixa ' +
          '<b>● Caixa aberto · desde HH:MM</b> — é por ela que você sabe que pode cobrar.' },

        { tipo: 'subtitulo', texto: 'Cobrar' },
        { tipo: 'passos', itens: [
          'Ache o veículo: <b>Ler QR do ticket</b> (o botão grande — aponte a câmera para o QR no centro do ticket), ' +
            '<b>Ler placa</b> ou <b>Digitar placa</b> para quem perdeu o ticket, ou <b>Digitar o código do ticket</b> ' +
            '(os 8 caracteres sob o QR) se o papel estiver amassado.',
          'O app mostra o veículo, o tempo de permanência e o <b>valor</b>. <b>Confira a placa com o carro.</b>',
          'Escolha a forma de pagamento.',
          'Em dinheiro, informe quanto recebeu — o app calcula o troco. Os botões de atalho são as <b>cédulas reais</b>.',
          'Toque em <b>Cobrar</b>.',
        ] },
        { tipo: 'p', texto:
          'Ao final, o app diz <b>até que horas o veículo pode sair</b>. É essa informação que você ' +
          'passa ao cliente ao devolver o troco.' },
        { tipo: 'aviso', tom: 'dica', titulo: 'Mensalista e credenciado não passam pelo caixa',
          texto: 'O app avisa isso antes de você tentar cobrar.' },
        { tipo: 'aviso', tom: 'dica', titulo: 'Prefira o QR quando o cliente tiver o ticket',
          texto:
            'Ele acha o ticket mesmo que a placa tenha sido lida errada na entrada. Se o ticket <b>já foi pago, ' +
            'cancelado ou encerrado</b>, o app mostra um cartão com a placa e os horários: <b>verde</b>, está ' +
            'resolvido e não há nada a cobrar; <b>âmbar</b>, o ticket não pode ser cobrado — se o carro ainda ' +
            'está no pátio, busque pela placa. O código nunca tem as letras O e I nem os números 0 e 1.' },

        { tipo: 'subtitulo', texto: 'Fechar o caixa' },
        { tipo: 'p', texto:
          'No fim do turno, toque em <b>Fechar caixa</b>, na faixa <b>Caixa aberto</b> logo abaixo do título ' +
          'da tela. A seta <b>←</b> no topo só volta para a tela anterior — não fecha o caixa. Se a faixa ' +
          'mostrar uma <b>data</b> ("desde 18/09 às 08:03"), o caixa ficou aberto de um dia para o outro: ' +
          'feche-o com a contagem antes de começar o turno novo.' },
        { tipo: 'aviso', tom: 'atencao', titulo: 'Conte o dinheiro antes de olhar a tela',
          texto:
            'O app pergunta quanto há na gaveta e <b>só depois</b> mostra o que o sistema esperava. ' +
            'Essa ordem é proposital: se você visse o número antes, acabaria confirmando o que a ' +
            'tela disse em vez de contar.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'vistoria',
      titulo: 'A vistoria do pátio',
      blocos: [
        { tipo: 'p', texto:
          'Serve para acertar a lista com a realidade: veículos que saíram sem a câmera ver ficam ' +
          'abertos no sistema para sempre, e a vistoria os encerra sem cobrança. Toque no ícone de ' +
          '<b>prancheta</b> no topo.' },
        { tipo: 'figura', src: 'app-vistoria.png', legenda: 'A vistoria em andamento, com o contador de quantos já foram conferidos.' },
        { tipo: 'passos', itens: [
          '<b>Abrir vistoria</b> — o app tira um retrato do pátio naquele instante.',
          'Ande pelo estacionamento. Em cada carro, toque em <b>Apontar</b> e fotografe a placa; o app marca o veículo como encontrado. Também dá para tocar direto na linha da lista.',
          'O topo mostra <b>quantos de quantos</b> você já conferiu.',
          'Toque em <b>Enviar</b> de tempos em tempos.',
        ] },
        { tipo: 'aviso', tom: 'dica', titulo: 'O app aguenta ficar sem sinal',
          texto:
            'As marcações ficam guardadas no celular e vão para o servidor quando a conexão voltar. ' +
            'Você não perde a caminhada.' },
        { tipo: 'p', texto:
          '<b>Encerrar a vistoria é do gestor.</b> Se o botão não aparece para você, é porque seu ' +
          'perfil não tem essa permissão — peça ao gestor, ou peça a ele que lhe conceda alçada ' +
          'para isso. Antes de encerrar, o app diz <b>quantos veículos serão encerrados sem ' +
          'cobrança</b>. Confira esse número.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'nao-faz',
      titulo: 'O que o app não faz',
      blocos: [
        { tipo: 'p', texto:
          'O app tem o que se usa <b>em pé, com o carro na frente</b>. O resto é trabalho de mesa:' },
        { tipo: 'tabela',
          cabecalho: ['O que', 'Onde fazer'],
          linhas: [
            ['Estornar um pagamento', 'no computador, com perfil de gestor'],
            ['Cadastrar mensalista ou credenciado', 'no computador'],
            ['Mudar tarifas', 'no computador'],
            ['Relatórios', 'no computador'],
          ] },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'problemas',
      titulo: 'Se algo der errado',
      blocos: [
        { tipo: 'tabela',
          cabecalho: ['O que aconteceu', 'O que fazer'],
          linhas: [
            ['"Sem conexão com o servidor"', 'confira o Wi-Fi ou os dados do celular; o app tenta de novo sozinho'],
            ['A lista não atualiza', 'puxe a tela para baixo'],
            ['A sessão expirou', 'entre de novo; o app avisa quando isso acontece'],
            ['A câmera do celular não abre', 'use <i>Digitar</i> — todo caminho tem alternativa'],
            ['O app não deixa criar um ticket', 'leia a mensagem: quase sempre é porque a placa já está no pátio'],
          ] },
        { tipo: 'aviso', tom: 'dica', titulo: 'A sessão dura o turno inteiro',
          texto: 'O app renova sozinho enquanto está aberto — você não vai ser desconectado no meio de uma fila.' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'responsabilidade',
      titulo: 'Uma palavra sobre responsabilidade',
      blocos: [
        { tipo: 'p', texto:
          'Tudo o que você faz pelo app fica registrado com <b>seu nome</b>, o <b>horário</b> e o ' +
          '<b>aparelho</b>: correções de placa, cobranças, isenções, tickets abertos à mão, vistorias.' },
        { tipo: 'p', texto:
          'Isso protege você. Quando houver dúvida sobre uma cobrança ou uma saída, o registro ' +
          'mostra o que foi feito e por quem — e é por isso que o PIN importa: sem ele, alguém pode ' +
          'agir no seu nome.' },
      ],
    },
  ],
};
